import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { formatDistanceToNow } from 'date-fns';
import { RefreshCcw, WifiOff, Edit, Save, X, Settings, Trash2, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConnectionHealthMonitor } from '../components/ConnectionHealthMonitor';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { formatDeviceId } from '../utils/deviceUtils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Navigation } from "@/components/Navigation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Device {
  id: string;
  device_id: string;
  name: string | null;
  status: "online" | "offline" | "unknown";
  signal: number | null;
  last_seen: string | null;
  lastSeen: string;
  location: string | null;
  hardware_type: string | null;
  ip_address: string | null;
  created_at: string | null;
  series: string | null;
}

interface FirmwareUpdate {
  id: number;
  device_id: string;
  firmware_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  firmware: {
    version: string;
    description: string;
  };
}

interface DeviceEditProps {
  device: Device;
  onSave: (deviceId: string, name: string, location: string) => Promise<void>;
  onCancel: () => void;
}

const DeviceEditDialog: React.FC<DeviceEditProps> = ({ device, onSave, onCancel }) => {
  const [name, setName] = useState(device.name || '');
  const [location, setLocation] = useState(device.location || '');
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(device.device_id, name, location);
      toast.success("Device details updated");
    } catch (error) {
      toast.error("Failed to update device details");
      console.error("Error updating device:", error);
    } finally {
      setIsSaving(false);
      onCancel();
    }
  };
  
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Edit Device Details</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <label htmlFor="device-name" className="text-sm font-medium">Device Name</label>
          <Input 
            id="device-name"
            value={name} 
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter device name"
          />
        </div>
        
        <div className="space-y-2">
          <label htmlFor="device-location" className="text-sm font-medium">Location</label>
          <Input 
            id="device-location"
            value={location} 
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Enter device location"
          />
        </div>
        
        <div className="text-xs text-muted-foreground">
          Device ID: {device.device_id}
        </div>
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <RefreshCcw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save Changes
        </Button>
      </div>
    </DialogContent>
  );
};

const NEW_SERIES_VALUE = '__new__';

const SeriesCell: React.FC<{
  device: Device;
  seriesOptions: string[];
  onSetSeries: (deviceId: string, series: string) => Promise<void>;
}> = ({ device, seriesOptions, onSetSeries }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newSeries, setNewSeries] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const current = (device.series || 'ALL').toUpperCase();

  const save = async (value: string) => {
    setIsSaving(true);
    try {
      await onSetSeries(device.device_id, value);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Select
        value={current}
        disabled={isSaving}
        onValueChange={(value) => {
          if (value === NEW_SERIES_VALUE) {
            setNewSeries('');
            setIsAdding(true);
            return;
          }
          if (value !== current) save(value);
        }}
      >
        <SelectTrigger className="h-8 w-[130px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {seriesOptions.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
          <SelectItem value={NEW_SERIES_VALUE}>New series…</SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={isAdding} onOpenChange={setIsAdding}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add new series</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              value={newSeries}
              onChange={(e) => setNewSeries(e.target.value.toUpperCase())}
              placeholder="e.g. S3"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              ALL = the device follows firmware released for all series.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsAdding(false)}>Cancel</Button>
            <Button
              disabled={!newSeries.trim() || isSaving}
              onClick={async () => {
                await save(newSeries.trim().toUpperCase());
                setIsAdding(false);
              }}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DeviceTable: React.FC<{
  devices: Device[];
  onEditDevice: (device: Device) => void;
  onDeleteDevice: (device: Device) => void;
  isEditDialogOpen: boolean;
  selectedDevice: Device | null;
  firmwareUpdates: FirmwareUpdate[];
  deletingDeviceId: string | null;
  seriesOptions: string[];
  onSetSeries: (deviceId: string, series: string) => Promise<void>;
}> = ({ devices, onEditDevice, onDeleteDevice, isEditDialogOpen, selectedDevice, firmwareUpdates, deletingDeviceId, seriesOptions, onSetSeries }) => {
  const formatDeviceName = (device: Device) => {
    const shortMacAddress = formatDeviceId(device.device_id, true);
    const displayName = device.name || `Device ${shortMacAddress}`;
    return (
      <div>
        <div className="font-medium">{displayName}</div>
        <div className="text-xs text-muted-foreground font-mono">{device.device_id}</div>
      </div>
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('IP address copied');
    } catch (err) {
      toast.error('Copy failed');
    }
  };

  const formatIpAddress = (device: Device) => {
    if (!device.ip_address) {
      return <span className="text-muted-foreground">—</span>;
    }
    const url = `http://${device.ip_address}/`;
    return (
      <div className="flex items-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm font-mono text-primary hover:underline"
           title="Open web interface"
        >
          {device.ip_address}
          <ExternalLink className="h-3 w-3" />
        </a>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
           onClick={() => copyToClipboard(device.ip_address ?? '')}
           title="Copy IP address"
        >
          <Copy className="h-3 w-3" />
           <span className="sr-only">Copy IP address</span>
        </Button>
      </div>
    );
  };


  const getDeviceStatus = (device: Device) => {
    const now = new Date().getTime();
    const lastSeenTime = device.last_seen ? new Date(device.last_seen).getTime() : 0;
    const isOnline = lastSeenTime && (now - lastSeenTime) <= 300000; // 5 minutes
    
    return {
      isOnline,
      lastSeenFormatted: device.last_seen 
        ? formatDistanceToNow(new Date(device.last_seen), { addSuffix: true })
        : 'Never'
    };
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>IP address</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Series</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {devices.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-4">
              No devices found
            </TableCell>
          </TableRow>
        ) : (
          devices.map(device => {
            const { isOnline, lastSeenFormatted } = getDeviceStatus(device);
            
            return (
              <TableRow key={device.id}>
                <TableCell>
                  {formatDeviceName(device)}
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Badge variant={isOnline ? "success" : "secondary"}>
                      {isOnline ? "Online" : "Offline"}
                    </Badge>
                    <div className="text-xs text-muted-foreground">
                      Last seen: {lastSeenFormatted}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{formatIpAddress(device)}</TableCell>
                <TableCell>{device.location || 'Unspecified'}</TableCell>
                <TableCell>
                  <SeriesCell
                    device={device}
                    seriesOptions={seriesOptions}
                    onSetSeries={onSetSeries}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Dialog open={isEditDialogOpen && selectedDevice?.id === device.id} onOpenChange={(open) => {
                      if (!open && selectedDevice?.id === device.id) {
                        // Handle close
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => onEditDevice(device)}
                        >
                          <Settings size={16} />
                          <span className="sr-only">Edit device</span>
                        </Button>
                      </DialogTrigger>
                    </Dialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 size={16} />
                          <span className="sr-only">Delete device</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Device</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {device.name || formatDeviceId(device.device_id)}? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deletingDeviceId === device.device_id}
                            onClick={(event) => {
                              event.preventDefault();
                              void onDeleteDevice(device);
                            }}
                          >
                            {deletingDeviceId === device.device_id ? (
                              <>
                                <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                Deleting…
                              </>
                            ) : 'Delete'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

const DeviceMonitor: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [firmwareUpdates, setFirmwareUpdates] = useState<FirmwareUpdate[]>([]);
  const [firmwareSeries, setFirmwareSeries] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connectionLost, setConnectionLost] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState<Device | null>(null);
  const [lastConnectionToast, setLastConnectionToast] = useState<Date | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const queryClient = useQueryClient();

  // Devices check in every ~2 min; allow one missed check-in before "offline".
  const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;
  const TOAST_COOLDOWN_MS = 60 * 1000;

  const fetchFirmwareUpdates = useCallback(async () => {
    try {
      console.log("Fetching firmware updates...");
      const { data, error } = await supabase
        .from('firmware_updates')
        .select(`
          *,
          firmware:firmwares(version, description)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching firmware updates:", error);
        return;
      }

      console.log("Firmware updates fetched:", data);
      setFirmwareUpdates(data || []);
    } catch (err) {
      console.error("Error fetching firmware updates:", err);
    }
  }, []);

  const fetchFirmwareSeries = useCallback(async () => {
    const { data, error } = await supabase.from('firmwares').select('series');
    if (error) {
      console.error('Error fetching firmware series:', error);
      return;
    }
    setFirmwareSeries((data || []).map(f => (f.series || '').toUpperCase()).filter(Boolean));
  }, []);

  const seriesOptions = React.useMemo(() => {
    const all = new Set<string>(['ALL']);
    devices.forEach(d => { if (d.series) all.add(d.series.toUpperCase()); });
    firmwareSeries.forEach(s => all.add(s));
    return Array.from(all).sort((a, b) => (a === 'ALL' ? -1 : b === 'ALL' ? 1 : a.localeCompare(b)));
  }, [devices, firmwareSeries]);

  const updateDeviceSeries = useCallback(async (deviceId: string, series: string) => {
    const value = series.trim().toUpperCase();
    const stored = value === 'ALL' ? null : value;
    const { error } = await supabase
      .from('devices')
      .update({ series: stored })
      .eq('device_id', deviceId);

    if (error) {
      console.error('Error updating device series:', error);
      toast.error('Failed to save series');
      return;
    }

    setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, series: stored } : d));
    toast.success(`Series set to ${value}`);
  }, []);

  const fetchDevices = useCallback(async () => {
    setError(null);
    
    try {
      console.log("Fetching device status...");
      setDebugInfo('Fetching devices from Supabase...');
      
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .order('last_seen', { ascending: false });
      
      console.log("Raw device data from Supabase:", data);
      console.log("Device query error:", error);
      
      if (error) {
        console.error("Supabase error:", error);
        setDebugInfo(`Error: ${error.message}`);
        throw error;
      }

      if (data) {
        console.log(`Found ${data.length} devices in database`);
        setDebugInfo(`Found ${data.length} devices in database`);
        
        const now = Date.now();
        
        const processedDevices = data.map(device => {
          console.log("Processing device:", device.device_id, "hardware_type:", device.hardware_type);
          
          const lastSeenDate = device.last_seen ? new Date(device.last_seen) : null;
          const lastSeenTimestamp = lastSeenDate ? lastSeenDate.getTime() : 0;
          
          let status: "online" | "offline" | "unknown";
          
          if (!lastSeenDate) {
            status = "unknown";
          } else {
            status = (now - lastSeenTimestamp) <= ONLINE_THRESHOLD_MS ? "online" : "offline";
          }
          
          const lastSeen = lastSeenDate 
            ? formatDistanceToNow(lastSeenDate, { addSuffix: true })
            : 'Never';

          return {
            id: device.id,
            device_id: device.device_id,
            name: device.name,
            status,
            signal: device.signal,
            last_seen: device.last_seen,
            lastSeen,
            location: device.location,
            hardware_type: device.hardware_type,
            ip_address: device.ip_address,
            created_at: device.created_at,
            series: device.series ?? null,
          } as Device;
        });
        
        console.log("Processed devices:", processedDevices);
        setDevices(processedDevices);
        setLastUpdateTime(new Date());
        setConnectionLost(false);
        setDebugInfo(`Successfully loaded ${processedDevices.length} devices`);
      } else {
        console.log("No devices found in database");
        setDebugInfo('No devices found in database');
        setDevices([]);
      }
    } catch (err: any) {
      console.error('Error fetching devices:', err);
      setError(err.message || 'Failed to load devices');
      setConnectionLost(true);
      setDebugInfo(`Error: ${err.message || 'Failed to load devices'}`);
    }
  }, [ONLINE_THRESHOLD_MS]);

  const { isLoading, refetch } = useQuery({
    queryKey: ['devices'],
    queryFn: async () => {
      await fetchDevices();
      await fetchFirmwareUpdates();
      await fetchFirmwareSeries();
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attempt) => Math.min(attempt * 1000, 30000),
  });

  // Device check-ins are no longer streamed over realtime (they generated
  // millions of messages a month). The 30s query refetch above keeps the
  // online/offline badges current, and React Query pauses it when the tab is
  // in the background.
  useEffect(() => {
    fetchDevices();
    fetchFirmwareUpdates();
    fetchFirmwareSeries();
    setLastUpdateTime(new Date());
    setConnectionLost(false);
  }, [fetchDevices, fetchFirmwareUpdates, fetchFirmwareSeries]);

  const handleRefresh = () => {
    refetch();
    toast.success('Refreshing device list...', {
      id: 'refreshing',
    });
  };

  const handleConnectionChange = (isConnected: boolean) => {
    if (isConnected && connectionLost) {
      console.log("Connection restored, refreshing data");
      refetch();
    }
  };

  const updateDeviceDetails = async (deviceId: string, name: string, location: string) => {
    try {
      const { error } = await supabase
        .from('devices')
        .update({ name, location })
        .eq('device_id', deviceId);
      
      if (error) throw error;
      
      setDevices(devices.map(device => 
        device.device_id === deviceId 
          ? { ...device, name, location }
          : device
      ));
    } catch (error) {
      console.error("Error updating device:", error);
      throw error;
    }
  };

  const handleDeleteDevice = async (device: Device) => {
    if (deletingDevice) return;

    setDeletingDevice(device);
    try {
      // Related commands / firmware update records are removed automatically (cascade)
      const { data, error } = await supabase
        .from('devices')
        .delete()
        .eq('device_id', device.device_id)
        .select('id');

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('The device could not be removed. Please refresh and try again.');
      }

      setDevices(prev => prev.filter(d => d.device_id !== device.device_id));

      toast.success("Device deleted successfully", {
        description: `${device.name || device.device_id} has been removed`
      });

      await queryClient.invalidateQueries({ queryKey: ['devices'] });
    } catch (err: any) {
      console.error('Error deleting device:', err);
      toast.error("Failed to delete device", {
        description: err.message || "An unknown error occurred"
      });
    } finally {
      setDeletingDevice(null);
    }
  };

  const handleEditDevice = (device: Device) => {
    setSelectedDevice(device);
    setIsEditDialogOpen(true);
  };

  const handleEditCancel = () => {
    setSelectedDevice(null);
    setIsEditDialogOpen(false);
  };

  // Separate and sort devices - fix the filtering logic to handle actual hardware_type values
  const masterDevices = devices
    .filter(device => {
      // Check if hardware_type contains "ESP32" without "Slave" (like "ESP32" only)
      const isMaster = !device.hardware_type || 
                      device.hardware_type === 'ESP32' || 
                      (device.hardware_type && device.hardware_type.includes('ESP32') && !device.hardware_type.includes('Slave'));
      console.log(`Device ${device.device_id} (${device.hardware_type}) is master: ${isMaster}`);
      return isMaster;
    })
    .sort((a, b) => {
      const nameA = a.name || `Device ${formatDeviceId(a.device_id, true)}`;
      const nameB = b.name || `Device ${formatDeviceId(b.device_id, true)}`;
      return nameA.localeCompare(nameB);
    });

  const slaveDevices = devices
    .filter(device => {
      // Check if hardware_type contains "Slave" (like "ESP32 Slave")
      const isSlave = device.hardware_type && device.hardware_type.includes('Slave');
      console.log(`Device ${device.device_id} (${device.hardware_type}) is slave: ${isSlave}`);
      return isSlave;
    })
    .sort((a, b) => {
      const nameA = a.name || `Device ${formatDeviceId(a.device_id, true)}`;
      const nameB = b.name || `Device ${formatDeviceId(b.device_id, true)}`;
      return nameA.localeCompare(nameB);
    });

  console.log("Master devices count:", masterDevices.length);
  console.log("Slave devices count:", slaveDevices.length);
  console.log("Total devices count:", devices.length);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Device Monitor</h1>
          <div className="flex items-center gap-2">
            <ConnectionHealthMonitor onConnectionChange={handleConnectionChange} />
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              className="flex items-center gap-2"
              disabled={isLoading}
            >
              <RefreshCcw className={isLoading ? "animate-spin" : ""} size={16} />
              Refresh
            </Button>
          </div>
        </div>

        {lastUpdateTime && (
          <div className="text-sm text-muted-foreground">
            Last updated: {formatDistanceToNow(lastUpdateTime, { addSuffix: true })}
          </div>
        )}

        {error && (
          <div className="text-red-500 p-4">{error}</div>
        )}

        {/* Master Devices Section */}
        <Card>
          <CardHeader>
            <CardTitle>Master Devices ({masterDevices.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading && devices.length === 0 ? (
              <div className="text-center py-4">Loading devices...</div>
            ) : (
              <DeviceTable
                devices={masterDevices}
                onEditDevice={handleEditDevice}
                onDeleteDevice={handleDeleteDevice}
                isEditDialogOpen={isEditDialogOpen}
                selectedDevice={selectedDevice}
                firmwareUpdates={firmwareUpdates}
                seriesOptions={seriesOptions}
                onSetSeries={updateDeviceSeries}
                deletingDeviceId={deletingDevice?.device_id ?? null}
              />
            )}
          </CardContent>
        </Card>

        {/* Slave Devices Section */}
        <Card>
          <CardHeader>
            <CardTitle>Slave Devices ({slaveDevices.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading && devices.length === 0 ? (
              <div className="text-center py-4">Loading devices...</div>
            ) : (
              <DeviceTable
                devices={slaveDevices}
                onEditDevice={handleEditDevice}
                onDeleteDevice={handleDeleteDevice}
                isEditDialogOpen={isEditDialogOpen}
                selectedDevice={selectedDevice}
                firmwareUpdates={firmwareUpdates}
                seriesOptions={seriesOptions}
                onSetSeries={updateDeviceSeries}
                deletingDeviceId={deletingDevice?.device_id ?? null}
              />
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        {selectedDevice && (
          <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
            if (!open) handleEditCancel();
          }}>
            <DeviceEditDialog
              device={selectedDevice}
              onSave={updateDeviceDetails}
              onCancel={handleEditCancel}
            />
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default DeviceMonitor;
